import React, { useEffect, useState } from 'react';
import { useBreadcrumbs, useUser } from '../hooks';
import { Header, Table, Label, Groupper, Input, Button, Pagination, Icon, Modal, Toolbar, Message, Toast, Field } from 'react-frontier';
import { Budget, Department, SetLoading, ExpenseType, ExpenseRejectReason } from '../SiteClasses';
import { addCommas, bindClick, getDepartmentName, formatCreatorName } from '../SiteUtil';
import { Dropdown } from 'semantic-ui-react';

import UserAccess from '../UserAccess';
import classNames from 'classnames';
import BudgetModal from '../components/BudgetModal';
import ApproveBudgetsModal from '../components/ApproveBudgetsModal';
import CoordinatorBudgetsReportModal from '../components/CoordinatorBudgetsReportModal';
import moment from 'moment';
import API from '../API';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 20;

interface BudgetsListProps{
	
}

enum Modals{
	INFO = 1,
	CREATE = 2,
	EDIT = 3,
	APPROVE = 4,
	CONFIRMATION_APPROVE = 5,
	CONFIRMATION_CANCEL = 6,
	CONFIRMATION_REJECT = 7,
	CONFIRMATION_PAID = 8,
	COORDINATOR_REPORT = 9,
}

enum StatusFilter{
	PENDING = 0,
	APPROVED = 1,
	REJECTED = 2,
}

var BudgetsList = (props: BudgetsListProps)=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [budgets, setBudgets] = useState<Budget []>(null);
	var [input, setInput] = useState<string>('');
	var [searchBudgets, setSearchBudgets] = useState<Budget[]>(null);
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [searching, setSearching] = useState<boolean>(false);
	var [loading, setLoading] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [departmentFilters, setDepartmentFilters] = useState<Department[]>([]);
	var [typeFilter, setTypeFilter] = useState<number>(null);
	var [statusFilters, setStatusFilters] = useState<StatusFilter[]>([]);
	var [types, setTypes] = useState<ExpenseType[]>(null);
	var [selectedBudget, setSelectedBudget] = useState<Budget>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [dateStart, setDateStart] = useState<number>(null);
	var [dateEnd, setDateEnd] = useState<number>(null);
	var [dateModal, setDateModal] = useState<boolean>(false);
	var [dateModalPrompts, setDateModalPrompts] = useState<string[]>(null);
	var [rejectReasonId, setRejectReasonId] = useState<number>(null);
	var [rejectReasons, setRejectReasons] = useState<ExpenseRejectReason[]>([]);

	useEffect(()=>{
		setTitle('Presupuestos');
		get();
		getTypes();
		getRejectReasons();
	}, []);

	useEffect(()=>{
		if(budgets !== null){
			setPage(0);
			get(0);
		}
	}, [departmentFilters, typeFilter, statusFilters]);

	var getTypes = ()=>{
		API.getExpenseTypes().then(res=>{
			if(res.error) return setLoadError(res.message);
			setTypes(res.data);
		}).catch(err=>{
			Toast.error('Hubo un error al cargar los tipos de gasto. (LETS-1)');
		});
	};

	var getRejectReasons = ()=>{
		API.getExpenseRejectReasons().then(res=>{
			if(res.error) return;
			setRejectReasons(res.data);
		}).catch(err=>{});
	};

	var get = (get_page?: number)=>{
		var p = get_page ?? page;
		var offset = p * PAGE_SIZE;
		var startDate = dateStart ? moment.unix(dateStart).startOf('day').unix() : null;
		var endDate = dateEnd ? moment.unix(dateEnd).endOf('day').unix() : null;
		
		setLoading(true);
		API.getBudgets(PAGE_SIZE, offset, false, departmentFilters, typeFilter, statusFilters, startDate, endDate).then(res=>{
			if(res.error) return setLoadError(res.message);
			return setBudgets(res.data);
		}).catch(err => {
			return setLoadError('Hubo un error al obtener los presupuestos. (GBS-1)');
		}).finally(()=>{
			setLoading(false);
		})
	};

	var onPageChange = (page: number)=>{
      setLoading(true)
		setPage(page);
		get(page);
	}

	var onSearchChange = (val: string)=>{
		setInput(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(val.length<3){
			return setSearchBudgets(null);
		}
		setSearchTimeout(setTimeout(()=>{
			search(val)
		}, 500));
	}

	var onCreate = (newBudget: Budget)=>{
		setBudgets(prevBudgets => [newBudget, ...prevBudgets]);
		setShownModal(null);
	}

	var onEdit = (updatedBudget: Budget)=>{
		setBudgets(prevBudgets => prevBudgets.map(b => 
			b.budget_id === updatedBudget.budget_id ? updatedBudget : b
		));
		setShownModal(null);
		setSelectedBudget(null);
	};

	var approve = (setLoading: SetLoading)=>{
		setLoading(true);
		API.approveBudget([selectedBudget.budget_id]).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setBudgets(prevBudgets => prevBudgets.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, approved: true } : b
			));
			if(searchBudgets){
				setSearchBudgets(prevSearch => prevSearch.map(b => 
					b.budget_id === selectedBudget.budget_id ? { ...b, approved: true } : b
				));
			}
			setShownModal(null);
			setSelectedBudget(null);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al aprobar el presupuesto. (ABS-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var cancel = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteBudget(selectedBudget.budget_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setBudgets(prevBudgets => prevBudgets.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, deleted: true } : b
			));
			if(searchBudgets){
				setSearchBudgets(prevSearch => prevSearch.map(b => 
					b.budget_id === selectedBudget.budget_id ? { ...b, deleted: true } : b
				));
			}
			setShownModal(null);
			setSelectedBudget(null);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al cancelar el presupuesto. (CBS-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var rejectBudget = (setLoading: SetLoading)=>{
		if(!rejectReasonId){
			setModalPrompts(['Selecciona una razón de rechazo']);
			return;
		}
		setLoading(true);
		API.rejectBudget(selectedBudget.budget_id, rejectReasonId).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha rechazado el presupuesto');
			var reasonName = rejectReasons.find(r => r.reason_id === rejectReasonId)?.reason;
			setBudgets(prevBudgets => prevBudgets.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, rejected: true, approved: false, reject_reason_name: reasonName } : b
			));
			if(searchBudgets){
				setSearchBudgets(prevSearch => prevSearch.map(b => 
					b.budget_id === selectedBudget.budget_id ? { ...b, rejected: true, approved: false, reject_reason_name: reasonName } : b
				));
			}
			setShownModal(null);
			setSelectedBudget(null);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al rechazar el presupuesto. (RBS-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var markPaid = (setLoading: SetLoading)=>{
		setLoading(true);
		API.paidBudget(selectedBudget.budget_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setBudgets(prevBudgets => prevBudgets.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, paid: true } : b
			));
			if(searchBudgets){
				setSearchBudgets(prevSearch => prevSearch.map(b => 
					b.budget_id === selectedBudget.budget_id ? { ...b, paid: true } : b
				));
			}
			setShownModal(null);
			setSelectedBudget(null);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al marcar el presupuesto como pagado. (PBS-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var search = (query: string)=>{
		setSearching(true);
		setSearchBudgets(null);
		API.searchBudgets(query).then(res=>{
			if (res.error) return setLoadError(res.message);
			setSearchBudgets(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al buscar el presupuesto. (SBS-1)');
		}).finally(()=>{
			setSearching(false);
		})
	}

	var onDateFilter = ()=>{
		if(!dateStart || !dateEnd) return setDateModalPrompts(['Debes seleccionar ambas fechas para filtrar']);
		if(dateEnd <= dateStart) return setDateModalPrompts(['La fecha de fin debe ser mayor que la fecha de inicio']);
		
		var startDate = moment.unix(dateStart).startOf('day').unix();
		var endDate = moment.unix(dateEnd).endOf('day').unix();
		
		setDateModal(false);
		setDateModalPrompts(null);
		setLoading(true);
		
		API.getBudgets(PAGE_SIZE, 0, false, departmentFilters, typeFilter, statusFilters, startDate, endDate).then(res=>{
			if(res.error) return setLoadError(res.message);
			setBudgets(res.data);
			setPage(0);
			setDateStart(startDate);
			setDateEnd(endDate);
		}).catch(err => {
			return setLoadError('Hubo un error al obtener los presupuestos. (GBS-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onResetFilters = ()=>{
		setDateStart(null);
		setDateEnd(null);
		setLoading(true);
		API.getBudgets(PAGE_SIZE, 0, false, departmentFilters, typeFilter, statusFilters, null, null).then(res=>{
			if(res.error) return setLoadError(res.message);
			setBudgets(res.data);
			setPage(0);
		}).catch(err => {
			return setLoadError('Hubo un error al obtener los presupuestos. (GBS-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};
	
	if(loadError) { return <Header text='Error' subtext={loadError} />};
	if(!budgets) { return <Header text='Cargando datos' loading/> };

	var shown_budgets = searchBudgets || budgets;

	return <div>
		<Groupper
			title={'Presupuestos'}
			titleRight={(
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button text='Opciones' color='black' iconRight='caret-down' iconRightStyle={{ marginRight: 0, marginLeft: 5 }} size='small'/>
				)}>
					<Dropdown.Menu>
					{hasAccess(UserAccess.EVENTS.CREATE_BUDGET) && (
						<Dropdown.Item text={'Agregar'} icon={'plus'} onClick={bindClick(setShownModal, Modals.CREATE)}/>
					)}
					{hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) && (
						<Dropdown.Item text={'Aprobar'} icon={'check'} onClick={bindClick(setShownModal, Modals.APPROVE)}/>
					)}
					<Dropdown.Item text={'Desglose'} icon={'file'} as={Link} to={'/budgets/details'}/>
					<Dropdown.Item text={'Reporte por coordinador'} icon={'download'} onClick={bindClick(setShownModal, Modals.COORDINATOR_REPORT)}/>
					</Dropdown.Menu>
				</Dropdown>
			)}
			style={{ marginTop: 20 }}
			width={1500}
		>
			<div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
				<Input placeholder='Buscar...' value={input} onChange={onSearchChange} style={{ flex: 1 }} />
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button 
						text='Tipo' 
						iconRight={typeFilter !== null ? 'asterisk' : null}
						iconRightStyle={{ color: 'brown', marginLeft: 5}}
						size='small'
						iconName='filter'
					/>
				)}>
					<Dropdown.Menu>
						<Dropdown.Item text='Limpiar filtro' icon='trash' onClick={() => setTypeFilter(null)} />
						{types && types.map(type => (
							<Dropdown.Item key={type.type_id} onClick={() => setTypeFilter(typeFilter === type.type_id ? null : type.type_id)}>
								<i className={classNames('icon circle', {
									'check': typeFilter === type.type_id,
									'outline': typeFilter !== type.type_id,
								})}></i>
								{type.type_name}
							</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button 
						text='Filtros' 
						iconRight={(departmentFilters.length > 0 || statusFilters.length > 0) ? 'asterisk' : null}
						iconRightStyle={{ color: 'brown', marginLeft: 5}}
						size='small'
						iconName='filter'
					/>
				)}>
					<Dropdown.Menu>
						<Dropdown.Item text='Limpiar todos' icon='trash' onClick={() => {
							setDepartmentFilters([]);
							setStatusFilters([]);
						}} />
						<Dropdown.Header content='Departamentos' />
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.OPERACIONES) 
									? prev.filter(d => d !== Department.OPERACIONES)
									: [...prev, Department.OPERACIONES]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.OPERACIONES),
								'square outline': !departmentFilters.includes(Department.OPERACIONES),
							})}></i>
							Operaciones
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.COMERCIAL) 
									? prev.filter(d => d !== Department.COMERCIAL)
									: [...prev, Department.COMERCIAL]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.COMERCIAL),
								'square outline': !departmentFilters.includes(Department.COMERCIAL),
							})}></i>
							Comercial
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.DESARROLLO) 
									? prev.filter(d => d !== Department.DESARROLLO)
									: [...prev, Department.DESARROLLO]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.DESARROLLO),
								'square outline': !departmentFilters.includes(Department.DESARROLLO),
							})}></i>
							Desarrollo
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.RECURSOS_HUMANOS) 
									? prev.filter(d => d !== Department.RECURSOS_HUMANOS)
									: [...prev, Department.RECURSOS_HUMANOS]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.RECURSOS_HUMANOS),
								'square outline': !departmentFilters.includes(Department.RECURSOS_HUMANOS),
							})}></i>
							Recursos Humanos
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.MARKETING) 
									? prev.filter(d => d !== Department.MARKETING)
									: [...prev, Department.MARKETING]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.MARKETING),
								'square outline': !departmentFilters.includes(Department.MARKETING),
							})}></i>
							Marketing
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setDepartmentFilters(prev => 
								prev.includes(Department.CONTABILIDAD) 
									? prev.filter(d => d !== Department.CONTABILIDAD)
									: [...prev, Department.CONTABILIDAD]
							);
						}}>
							<i className={classNames('icon', {
								'check square': departmentFilters.includes(Department.CONTABILIDAD),
								'square outline': !departmentFilters.includes(Department.CONTABILIDAD),
							})}></i>
							Contabilidad
						</Dropdown.Item>
						<Dropdown.Divider />
						<Dropdown.Header content='Estatus' />
						<Dropdown.Item onClick={() => {
							setStatusFilters(prev => 
								prev.includes(StatusFilter.PENDING) 
									? prev.filter(s => s !== StatusFilter.PENDING)
									: [...prev, StatusFilter.PENDING]
							);
						}}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(StatusFilter.PENDING),
								'square outline': !statusFilters.includes(StatusFilter.PENDING),
							})}></i>
							Pendiente
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setStatusFilters(prev => 
								prev.includes(StatusFilter.APPROVED) 
									? prev.filter(s => s !== StatusFilter.APPROVED)
									: [...prev, StatusFilter.APPROVED]
							);
						}}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(StatusFilter.APPROVED),
								'square outline': !statusFilters.includes(StatusFilter.APPROVED),
							})}></i>
							Aprobado
						</Dropdown.Item>
						<Dropdown.Item onClick={() => {
							setStatusFilters(prev => 
								prev.includes(StatusFilter.REJECTED) 
									? prev.filter(s => s !== StatusFilter.REJECTED)
									: [...prev, StatusFilter.REJECTED]
							);
						}}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(StatusFilter.REJECTED),
								'square outline': !statusFilters.includes(StatusFilter.REJECTED),
							})}></i>
							Rechazado
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button 
						text='Fecha' 
						iconRight={(dateStart && dateEnd) ? 'asterisk' : null}
						iconRightStyle={{ color: 'brown', marginLeft: 5}}
						size='small'
						iconName='calendar'
					/>
				)}>
					<Dropdown.Menu>
						<Dropdown.Item icon='calendar' text='Filtrar por fecha' onClick={bindClick(setDateModal, true)} />
						<Dropdown.Item icon='close' text='Borrar filtro' onClick={() => onResetFilters()} />
					</Dropdown.Menu>
				</Dropdown>
			</div>
			<Table 
				striped 
				fitted
				style={{ marginTop: 15 }}
				headers={['ID', 'Titulo', 'Creador', 'Evento', 'Tipo', 'Departamento', 'Deposito', 'Estatus', 'Monto', <Icon name='wrench' />]}
				centeredIndexes={[3]}
				footer={(!!shown_budgets && shown_budgets.length >= PAGE_SIZE || page !== 0) && (
					<Table.Cell colSpan={10} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={shown_budgets.length>=PAGE_SIZE} />
						</div>
					</Table.Cell>
				)}
			>
				{searching || loading ? (
				<Table.Cell row className='empty' colSpan={8}>
						<Header loading text='Cargando datos' />
					</Table.Cell>
				) : (
					shown_budgets.length !== 0 ? (
						shown_budgets.map(b => (
							<Table.Row
								key={b.budget_id}
							collapsingIndexes={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9]}
							centeredIndexes={[3, 7]}
							data={[
								b.budget_id,
								b.budget_title,
								formatCreatorName(b.user_creator_name),
								b.event_name || <Icon name='minus'/>,
								b.type_name,
								b.department_name ? <Label style={{ width: 80, textAlign: 'center', padding: 3, fontSize: 11 }} color='orange'>{getDepartmentName(b.department_id)}</Label> : <Icon name='minus'/>,
								b.date_deposit ? moment.unix(b.date_deposit).format('DD/MM/YYYY') : <Icon name='minus'/>,
								<Label style={{ width: 80, textAlign: 'center', padding: '2px 4px', fontSize: 11 }} color={b.deleted ? 'red' : b.rejected ? 'red' : b.paid ? 'green' : b.approved ? 'green' : 'yellow'}>
									{b.deleted ? 'Eliminado' : b.rejected ? 'Rechazado' : b.paid ? 'Pagado' : b.approved ? 'Aprobado' : 'Pendiente'}
								</Label>,
								'$' + addCommas(b.amount),
									<Dropdown icon={(
										<div className="fr label" style={{ padding: '3px 10px' }}>
											<Icon name='ellipsis' style={{ margin: 0 }} />
										</div>
									)} className='icon'>
										<Dropdown.Menu>
											<Dropdown.Item text='Info' icon='eye' onClick={()=>{
												setSelectedBudget(b);
												setShownModal(Modals.INFO);
											}}/>
											{!b.rejected && !b.deleted && !b.paid && hasAccess(UserAccess.EVENTS.CREATE_BUDGET) ? (
												<Dropdown.Item text='Editar' icon='pencil' onClick={()=>{
													setSelectedBudget(b);
													setShownModal(Modals.EDIT);
												}}/>
											) : null}
											{!b.rejected && !b.deleted && !b.paid && hasAccess(UserAccess.EVENTS.REJECT_EXPENSE) ? (
												<Dropdown.Item text='Rechazar' icon='ban' onClick={()=>{
													setSelectedBudget(b);
													setRejectReasonId(null);
													setModalPrompts(null);
													setShownModal(Modals.CONFIRMATION_REJECT);
												}}/>
											) : null}
											{!b.deleted && !b.paid && b.approved && !b.rejected && hasAccess(UserAccess.ACCOUNTING.PAY_EXPENSE) ? (
												<Dropdown.Item text='Marcar pagado' icon='check circle' onClick={()=>{
													setSelectedBudget(b);
													setModalPrompts(null);
													setShownModal(Modals.CONFIRMATION_PAID);
												}}/>
											) : null}
										</Dropdown.Menu>
									</Dropdown>,
								]}
							/>
						))
					) : (
					<Table.Cell row className='empty' colSpan={8}>
						No se encontraron registros
					</Table.Cell>
					)
				)}
			</Table>
		</Groupper>
		<BudgetModal
			open={shownModal === Modals.CREATE || shownModal === Modals.EDIT}
			onClose={()=> {
				setShownModal(null);
				setSelectedBudget(null);
			}}
			onCreate={(b) => onCreate(b)}
			onUpdate={(b) => onEdit(b)}
			budgetToEdit={shownModal === Modals.EDIT ? selectedBudget : null}
			date_id={shownModal === Modals.EDIT ? selectedBudget?.date_id : null}
			event_name={shownModal === Modals.EDIT ? selectedBudget?.event_name : null}
		/>
		<ApproveBudgetsModal
			open={shownModal === Modals.APPROVE}
			onClose={bindClick(setShownModal, null)}
			onComplete={(approvedIds) => {
				setBudgets(prevBudgets => prevBudgets.map(b => 
					approvedIds.includes(b.budget_id) ? { ...b, approved: true } : b
				));
				if(searchBudgets) {
					setSearchBudgets(prevSearch => prevSearch.map(b => 
						approvedIds.includes(b.budget_id) ? { ...b, approved: true } : b
					));
				}
				setShownModal(null);
			}}
		/>
		<Modal open={shownModal === Modals.INFO} size='tiny' onClose={bindClick(setShownModal, null)}>
			<Modal.Header>Información de presupuesto</Modal.Header>
			<Toolbar stretch style={{ height: 40 }}>
				{!selectedBudget?.approved && !selectedBudget?.deleted && !selectedBudget?.rejected ? <Toolbar.Item text='Aprobar' iconName='check' onClick={bindClick(setShownModal, Modals.CONFIRMATION_APPROVE)}/> : null}
				{!selectedBudget?.rejected && !selectedBudget?.deleted && !selectedBudget?.paid && hasAccess(UserAccess.EVENTS.REJECT_EXPENSE) ? <Toolbar.Item text='Rechazar' iconName='ban' onClick={()=>{ setRejectReasonId(null); setModalPrompts(null); setShownModal(Modals.CONFIRMATION_REJECT); }}/> : null}
				{!selectedBudget?.deleted && !selectedBudget?.paid && !!selectedBudget?.approved && !selectedBudget?.rejected && hasAccess(UserAccess.ACCOUNTING.PAY_EXPENSE) ? <Toolbar.Item text='Marcar pagado' iconName='check-circle' onClick={()=>{ setModalPrompts(null); setShownModal(Modals.CONFIRMATION_PAID); }}/> : null}
				{!selectedBudget?.deleted && !selectedBudget?.paid ? <Toolbar.Item text='Cancelar' iconName='times' onClick={bindClick(setShownModal, Modals.CONFIRMATION_CANCEL)}/> : null}
			</Toolbar>
			{selectedBudget && <Modal.Content style={{ paddingBottom: 0 }}>
				<Table
					striped
					fitted
					details
					data={[
						['ID', selectedBudget.budget_id],
						['Título', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],
						['Departamento', selectedBudget.department_name ? <Label style={{ width: 80, textAlign: 'center', padding: 3, fontSize: 11 }} color='orange'>{getDepartmentName(selectedBudget.department_id)}</Label> : <Icon name='minus'/>],
						['Creador', formatCreatorName(selectedBudget.user_creator_name) || <Icon name='minus'/>],
						['Fecha de creación', selectedBudget.date_created ? moment.unix(selectedBudget.date_created).format('DD/MM/YYYY HH:mm') : <Icon name='minus'/>],
						...(selectedBudget.date_id ? [
							['Evento', selectedBudget.date_id],
							['Nombre evento', selectedBudget.event_name || <Icon name='minus'/>],
							['Ciudad', selectedBudget.city || <Icon name='minus'/>],
						] : []),
						['Monto', '$' + addCommas(selectedBudget.amount)],
						['Estatus', <div className={classNames("fr label", {
							green: (selectedBudget.approved && !selectedBudget.deleted && !selectedBudget.rejected && !selectedBudget.paid) || (selectedBudget.paid && !selectedBudget.deleted),
							red: selectedBudget.deleted || selectedBudget.rejected,
							yellow: !selectedBudget.approved && !selectedBudget.deleted && !selectedBudget.rejected && !selectedBudget.paid
						})}>
							{selectedBudget.deleted ? 'Eliminado' : selectedBudget.rejected ? 'Rechazado' : selectedBudget.paid ? 'Pagado' : selectedBudget.approved ? 'Aprobado' : 'Pendiente'}
						</div>],
						...(selectedBudget.rejected && !selectedBudget.deleted ? [['Razón de rechazo', selectedBudget.reject_reason_name || <Icon name='minus'/>]] : []),
						['Descripción', selectedBudget.description || <Icon name='minus'/>],
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShownModal, null)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CONFIRMATION_APPROVE || shownModal === Modals.CONFIRMATION_CANCEL} size='tiny' onClose={bindClick(setShownModal, Modals.INFO)}>
			<Modal.Header>{shownModal === Modals.CONFIRMATION_APPROVE ? 'Aprobar presupuesto' : 'Cancelar presupuesto'}</Modal.Header>
			<Modal.Content>
				<Header text={shownModal === Modals.CONFIRMATION_APPROVE ? '¿Marcar pago como aprobado?' : '¿Marcar pago como cancelado?'}/>
				<Table
					details
					striped
					data={[
						['ID', selectedBudget?.budget_id],
						['Título', selectedBudget?.budget_title],
						['Monto', '$' + addCommas(selectedBudget?.amount)],
					]}
				/>
				<Message type="error" list={modalPrompts}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cancelar' onClick={bindClick(setShownModal, Modals.INFO)} />
				<Button color={'black'} text='Guardar' onClick={shownModal === Modals.CONFIRMATION_APPROVE ? approve : cancel}/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CONFIRMATION_REJECT} size='tiny' onClose={bindClick(setShownModal, Modals.INFO)}>
			<Modal.Header>Rechazar presupuesto</Modal.Header>
			<Modal.Content>
				<Header text='¿Rechazar este presupuesto?' subtext='El presupuesto será marcado como rechazado y se notificará al creador con el motivo de rechazo.' />
				<Table
					details
					striped
					data={[
						['ID', selectedBudget?.budget_id],
						['Título', selectedBudget?.budget_title],
						['Monto', '$' + addCommas(selectedBudget?.amount)],
					]}
				/>
				<Dropdown
					selection
					fluid
					placeholder='Selecciona el motivo de rechazo'
					value={rejectReasonId}
					onChange={(e, { value }) => setRejectReasonId(value as number)}
					options={rejectReasons.map(r => ({
						key: r.reason_id,
						value: r.reason_id,
						text: r.reason,
					}))}
					style={{ marginTop: 15 }}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cancelar' onClick={bindClick(setShownModal, Modals.INFO)} />
				<Button color='red' text='Rechazar' onClick={rejectBudget}/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CONFIRMATION_PAID} size='tiny' onClose={bindClick(setShownModal, Modals.INFO)}>
			<Modal.Header>Marcar presupuesto como pagado</Modal.Header>
			<Modal.Content>
				<Header text='¿Marcar este presupuesto como pagado?'/>
				<Table
					details
					striped
					data={[
						['ID', selectedBudget?.budget_id],
						['Título', selectedBudget?.budget_title],
						['Monto', '$' + addCommas(selectedBudget?.amount)],
					]}
				/>
				<Message type="error" list={modalPrompts}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cancelar' onClick={bindClick(setShownModal, Modals.INFO)} />
				<Button color={'black'} text='Marcar pagado' onClick={markPaid}/>
			</Modal.Actions>
		</Modal>
		<Modal open={dateModal} size='mini' onClose={bindClick(setDateModal, false)}>
			<Modal.Header>Filtrar por fecha</Modal.Header>
			<Modal.Content>
				<Field amount={2}>
					<Input 
						label='Fecha inicio' 
						value={dateStart} 
						onChange={setDateStart}
						style={{ width: '100%'}}
						calendar={{ 
							date: moment().startOf('isoWeek').unix(), 
							mode: 'date', 
							format: 'DD/MM/YYYY' 
						}} 
					/>
					<Input 
						label='Fecha fin' 
						value={dateEnd}
						style={{ width: '100%'}}
						onChange={setDateEnd} 
						calendar={{ 
							date: moment().endOf('isoWeek').unix(), 
							mode: 'date', 
							format: 'DD/MM/YYYY' 
						}} 
					/>
				</Field>
				<Message type="error" list={dateModalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setDateModal, false)} />
				<Button text='Guardar' color='black' onClick={onDateFilter} />
			</Modal.Actions>
		</Modal>
		<CoordinatorBudgetsReportModal
			open={shownModal === Modals.COORDINATOR_REPORT}
			onClose={bindClick(setShownModal, null)}
		/>
	</div>
};

export default BudgetsList;
